// Deterministic avatar initials + colors — the single React-side source.
//
// Mirrors getInitial()/generateColorGradient()/getInitialsHtml() in
// legacy_js/layout_main.js (kept there as a copy only because plain scripts
// can't import modules; the parity test pins the two together). ProfileImage
// and Avatar both import from here so the math lives once on the React side.
//
// Parity notes:
//   - a missing name part contributes a SPACE (char 32) to the hue, exactly
//     like the legacy `getInitial(lastName || ' ')` call; both parts missing
//     renders "?" like getInitialsHtml();
//   - when only a single `name` string is available it splits as first word /
//     rest ("Misbah ur Rehman" → "MU"). A multi-word FIRST name can't be
//     reconstructed from that ("Jean Paul Belmondo" → "JP", but the DB-backed
//     portrait shows "JB") — callers that have the separate fields should use
//     getInitialPairFromParts()/the Avatar firstName+lastName props instead.

export const getInitial = (name) => name && String(name).toUpperCase().charAt(0);

// Legacy-equivalent [first, last] initial pair from the separate name fields.
export const getInitialPairFromParts = (firstName, lastName) => {
    let first = getInitial(firstName || ' ') || ' ';
    const last = getInitial(lastName || ' ') || ' ';
    if (first === ' ' && last === ' ') {
        first = '?';
    }
    return [first, last];
};

// Best-effort pair from a single full-name string (first word / rest).
export const getInitialPair = (name) => {
    const parts = String(name ?? '')
        .trim()
        .split(/\s+/)
        .filter(Boolean);
    return getInitialPairFromParts(parts[0], parts.slice(1).join(' '));
};

// Display initials: "Shoaib Ahmed" → "SA", "Cher" → "C", "" → "?".
export const getInitials = (name) => getInitialPair(name).join('').trim();

// The 17 named avatar gradients from the design system (Avatars card,
// "Avatar colors — no image"): indigo–purple, bronze, orchid, teal, sunset,
// green, amber, blue, raspberry, slate, violet, lagoon, terracotta,
// periwinkle, plum, moss, fuchsia.
export const AVATAR_GRADIENTS = [
    'linear-gradient(135deg, #6172F3, #9E42FF)',
    'linear-gradient(135deg, #B7803F, #C96E63)',
    'linear-gradient(135deg, #C77DD8, #C0427C)',
    'linear-gradient(135deg, #2E7BA6, #4FB0A5)',
    'linear-gradient(135deg, #E0724A, #C94F63)',
    'linear-gradient(135deg, #5FA84E, #2E8C6A)',
    'linear-gradient(135deg, #F2A93B, #E0724A)',
    'linear-gradient(135deg, #4E7CE8, #3AA6C9)',
    'linear-gradient(135deg, #D9536B, #A8386E)',
    'linear-gradient(135deg, #7B8794, #4B5563)',
    'linear-gradient(135deg, #9E77ED, #6941C6)',
    'linear-gradient(135deg, #2EAFA0, #1B7F8C)',
    'linear-gradient(135deg, #C9744F, #9C5432)',
    'linear-gradient(135deg, #5B8DEF, #7B61FF)',
    'linear-gradient(135deg, #B0518E, #6E3AA8)',
    'linear-gradient(135deg, #3E9B62, #87B440)',
    'linear-gradient(135deg, #7A5AF8, #E9526F)',
];

// Deterministic palette pick from two initial characters — identical to the
// legacy function.
export const generateColorGradient = (char1, char2) => {
    if (!char1 || char1.length !== 1) {
        return null;
    }
    const secondChar = char2 && char2.length === 1 ? char2 : char1;
    const paletteIndex = (char1.charCodeAt(0) + secondChar.charCodeAt(0)) % AVATAR_GRADIENTS.length;
    return AVATAR_GRADIENTS[paletteIndex];
};

// Gradient from a full-name string.
export const generateAvatarGradient = (name) => {
    const [char1, char2] = getInitialPair(name);
    return generateColorGradient(char1, char2);
};
